import React, { useState } from 'react'
import { toast } from 'react-toastify'

import { Icon, Popover } from '@clientbase/clientbase-library'
import { Stack } from '@mui/material'
import { baseURL } from 'api/axios'
import { DataService } from 'api/DataService'
import axios from 'axios'
import { useAuthContext } from 'context/AuthContext'
import copy from 'copy-to-clipboard'
import { parseCookies } from 'nookies'

import Can from 'components/Can'
import ArchiveReasonDialog from 'components/v2/ArchiveReasonDialog'

import { formatCpfCnpj, getDocumentType } from 'utils'
import {
  getPortalActivateAccountUrl,
  getPortalAutoLoginUrl,
  getPortalBaseUrl
} from 'utils/domain-urls'

import { theme } from 'styles/theme'

import CustomerActionsMenu from '../CustomerDetail/components/CustomerActionsMenu'
import * as S from './CustomerActionsPopover.styles'

export interface CustomerActionsPopoverProps {
  customerUuid: string
  customerName: string
  customerDocument?: string
  customerCategory?: string
  status?: string
  onDelete?: () => void
  onArchive?: () => void
  onUnarchive?: () => void
  onOpenHistoric?: () => void
  onActionComplete?: () => void
  triggerButtonStyle?: React.CSSProperties
  isTable?: boolean
}

const CustomerActionsPopover: React.FC<CustomerActionsPopoverProps> = ({
  customerUuid,
  customerName,
  customerDocument,
  customerCategory,
  status,
  onDelete,
  onArchive,
  onUnarchive,
  onOpenHistoric,
  onActionComplete,
  triggerButtonStyle,
  isTable
}) => {
  const { ['nextauth.token']: token } = parseCookies()
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [isArchiveDialogOpen, setIsArchiveDialogOpen] = useState(false)
  const { user } = useAuthContext()

  const handleToggleAnchor = (event?: React.MouseEvent<HTMLElement> | null) => {
    setAnchorEl(event ? event.currentTarget : null)
  }

  const onButtonClick = (fn?: () => void) => {
    fn?.()
    handleToggleAnchor()
    onActionComplete?.()
  }

  const handleOpenArchiveDialog = () => {
    handleToggleAnchor()
    setIsArchiveDialogOpen(true)
  }

  const handleArchiveSuccess = () => {
    onArchive?.()
    onActionComplete?.()
  }

  const getPortalToken = async () => {
    try {
      const url = `${baseURL}/v2/customer/${customerUuid}/token_portal`
      const response = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      })

      return response.data as { token: string }
    } catch {
      return null
    }
  }

  const handleEnterInPortal = async () => {
    const data = await getPortalToken()

    if (!data) {
      toast.error('Erro ao criar link de acesso o Portal.')
      return
    }

    const url = getPortalAutoLoginUrl(data.token, user?.subdomain)
    window.open(url, '_blank')
  }

  const handleOpenFichaCadastral = () => {
    const url = `/clientes/ficha-cadastral/${customerUuid}`
    window.open(url, '_blank')
  }

  const handleCopyPortalInfo = async () => {
    toast.promise(
      async () => {
        const data = await DataService({
          url: `/v3/customer_users/reset_password_token?customer_uuid=${customerUuid}`,
          type: 'GET'
        })

        const documentType = getDocumentType(customerDocument || '')
        const portalBaseUrl = getPortalBaseUrl(user?.subdomain)

        const message = `Olá, ${customerName}.
        Para acessar o nosso Portal de clientes, basta entrar em ${portalBaseUrl}, informar seu ${documentType}: ${formatCpfCnpj(
          customerDocument || ''
        )} e sua senha. ${
          !data?.error
            ? `Caso não tenha cadastrado uma senha ou não se lembre dela, basta acessar ${getPortalActivateAccountUrl(
                data?.data?.token,
                user?.subdomain
              )} para cadastrá-la.`
            : ''
        } Nele, você consegue acessar todas as suas faturas, comprovantes de pagamento, arquivos, contratos, notas fiscais, atualizar dados cadastrais e muito mais.`
        copy(message)
      },
      {
        pending: 'Gerando instruções de acesso...',
        success: 'Copiado com sucesso!'
      }
    )
  }

  return (
    <Can permission="customers:manage">
      <>
        <Stack
          sx={{
            cursor: 'pointer',
            borderRadius: '8px',
            alignItems: 'center',
            backgroundColor: theme.palette.neutral[800],
            padding: '6px',
            mt: isTable ? '6px' : '0px',
            ml: isTable ? '6px' : '0px',
            ...triggerButtonStyle
          }}
          onClick={handleToggleAnchor}
        >
          <Icon size={3} icon="moreHoriz" />
        </Stack>

        <Popover
          anchorEl={anchorEl}
          anchorOrigin={{ horizontal: 'right', vertical: 'top' }}
          keepMounted={false}
          onClose={() => handleToggleAnchor(null)}
          open={Boolean(anchorEl)}
          sx={{ maxWidth: '950px', minWidth: '300px', mt: 5 }}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        >
          <S.MenuItemsWrapper>
            <CustomerActionsMenu
              status={status}
              handleClickDelete={() => onButtonClick(onDelete)}
              handleClickArchive={handleOpenArchiveDialog}
              handleClickUnarchive={() => onButtonClick(onUnarchive)}
              handleOpenFichaCadastral={() =>
                onButtonClick(handleOpenFichaCadastral)
              }
              handleEnterInPortal={() => onButtonClick(handleEnterInPortal)}
              handleCopyPortalInfo={() => onButtonClick(handleCopyPortalInfo)}
              handleOpenHistoric={() => {
                handleToggleAnchor(null)
                onOpenHistoric?.()
              }}
            />
          </S.MenuItemsWrapper>
        </Popover>

        <ArchiveReasonDialog
          isOpen={isArchiveDialogOpen}
          onClose={() => setIsArchiveDialogOpen(false)}
          customerUuid={customerUuid}
          customerName={customerName}
          customerCategory={customerCategory}
          onSuccess={handleArchiveSuccess}
        />
      </>
    </Can>
  )
}

export default CustomerActionsPopover
